import { Paper, Text } from "@mantine/core";
import type { WhiteboardDocumentId } from "@src/components/collaboration/document-id";
import { useId } from "react";
import { LinkInputWithCopy } from "./link-input-with-copy";
import { PermissionCombobox, type PermissionType } from "./permission-combobox";
import { SharePanelQrSection } from "./share-panel-qr-section";
import { getSharePanelQrFilename } from "./share-panel-qr-utils";
import { SwitchCard } from "./switch-card";
import { TemplateCheckbox } from "./template-checkbox";

type PublishTabContentProps = {
	boardId: WhiteboardDocumentId;
	isPublished: boolean;
	isLoading: boolean;
	permission: PermissionType;
	clipboardPublishedLink: string | null;
	entityName: string;
	entityLabel: "card" | "board";
	isLinkCopiedTooltipOpen: boolean;
	onCopyTooltipTrigger: () => void;
	onSwitchChange: (checked: boolean) => void;
	onPermissionChange: (value: PermissionType) => void;
	allowTemplate?: boolean;
	onTemplateChange?: (isChecked: boolean) => void;
	showTemplateOption?: boolean;
};

export function PublishTabContent({
	boardId,
	isPublished,
	isLoading,
	permission,
	clipboardPublishedLink,
	entityName,
	entityLabel,
	isLinkCopiedTooltipOpen,
	onCopyTooltipTrigger,
	onSwitchChange,
	onPermissionChange,
	allowTemplate = false,
	onTemplateChange,
	showTemplateOption = false,
}: PublishTabContentProps) {
	const publishQrCodeId = useId();
	const canShowQrCode = isPublished && Boolean(clipboardPublishedLink);

	return (
		<div className="space-y-6 pt-0">
			<SwitchCard
				title="Publish to Web"
				description={
					isPublished
						? `Your ${entityLabel} is currently live at a public link`
						: "Publish to share a view-only or editable link"
				}
				checked={isPublished}
				onChange={onSwitchChange}
				disabled={isLoading}
			/>

			<div className="space-y-2">
				<div className="flex justify-between items-end">
					<Text
						size="xs"
						fw={700}
						c="dimmed"
						className="uppercase tracking-widest"
					>
						Collaborative Public Link
					</Text>
				</div>
				<LinkInputWithCopy
					value={clipboardPublishedLink}
					placeholder={
						isLoading ? "" : "Link will appear here after publishing"
					}
					disabled={isLoading || !isPublished}
					isLoading={isLoading}
					isActive={isPublished}
					tooltipOpen={isLinkCopiedTooltipOpen}
					onTooltipTrigger={onCopyTooltipTrigger}
				/>
			</div>

			<div className="space-y-4">
				<Paper
					withBorder
					p="md"
					radius="md"
					className="bg-surface-subtle shadow-sm border-border-subtle transition-all duration-300 hover:shadow-md"
				>
					<div className="flex flex-col gap-4">
						<div className="flex justify-between items-center gap-3">
							<Text size="md" fw={600} className="text-text-primary">
								Guest Accessibility
							</Text>
							<PermissionCombobox
								value={permission}
								onChange={onPermissionChange}
								disabled={isLoading}
							/>
						</div>

						{showTemplateOption && onTemplateChange && (
							<TemplateCheckbox
								checked={allowTemplate}
								onChange={onTemplateChange}
								disabled={isLoading || !isPublished}
							/>
						)}

						{canShowQrCode && clipboardPublishedLink && (
							<SharePanelQrSection
								boardId={boardId}
								qrCodeId={publishQrCodeId}
								link={clipboardPublishedLink}
								title={`Scan to open this published ${entityLabel}`}
								description={`Display this QR code on a screen so guests can open the published ${entityLabel} without typing the public link.`}
								withOpenButton={false}
								filename={getSharePanelQrFilename(
									entityName,
									entityLabel === "card"
										? "published-card-qr"
										: "published-board-qr",
								)}
								downloadAriaLabel={"Download published link as SVG QR code"}
								analyticsContext="publish"
							/>
						)}
					</div>
				</Paper>
			</div>
		</div>
	);
}
